import React, { useState, useCallback } from 'react';
import { useSession } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { Checkbox } from 'components/ui/checkbox';
import { Label } from 'components/ui/label';
import { Button } from 'components/ui/button';
import { Loader2 } from 'lucide-react';
import { extractTranslatableContent, assembleWithTranslation } from '../utils/translationUtils';
import * as Sentry from '@sentry/react';

const LANGUAGES = [
  { code: 'fr', label: 'Frans' },
  { code: 'en', label: 'Engels' },
  { code: 'tr', label: 'Turks' },
  { code: 'uk', label: 'Oekraïens' },
  { code: 'ary', label: 'Arabisch' },
  { code: 'pl', label: 'Pools' },
  { code: 'es', label: 'Spaans' },
  { code: 'de', label: 'Duits' },
  { code: 'ro', label: 'Roemeens' },
  { code: 'ru', label: 'Russisch' },
] as const;

interface TranslateToolbarProps {
  emailContent: string;
  onTranslate: (html: string) => void;
  disabled?: boolean;
}

export const TranslateToolbar: React.FC<TranslateToolbarProps> = ({ emailContent, onTranslate, disabled }) => {
  const { session } = useSession();
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [isTranslating, setIsTranslating] = useState(false);

  const toggleLanguage = useCallback((code: string) => {
    setSelectedLanguages(prev =>
      prev.includes(code) ? prev.filter(l => l !== code) : [...prev, code]
    );
  }, []);

  const handleTranslate = useCallback(async () => {
    if (selectedLanguages.length === 0 || !emailContent.trim()) return;

    setIsTranslating(true);
    try {
      const cleanContent = extractTranslatableContent(emailContent);

      const response = await fetchWithAuth(
        `${API_URL}/mail-templates/translate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            htmlContent: cleanContent,
            targetLanguages: selectedLanguages,
            sourceLanguage: 'nl',
          }),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Vertaling mislukt (${response.status})`);
      }

      const data = await response.json();
      const assembled = assembleWithTranslation(emailContent, data.translations);
      onTranslate(assembled);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'mass-mail-translate' },
        extra: { selectedLanguages },
      });
      console.error('Vertaling mislukt:', error);
    } finally {
      setIsTranslating(false);
    }
  }, [emailContent, selectedLanguages, session, onTranslate]);

  return (
    <div className={`space-y-3 p-4 border border-gray-200 rounded-lg bg-white transition-opacity ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
      <h4 className="text-sm font-medium text-gray-900">Vertaling</h4>

      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-sm text-amber-800">
          <span className="font-medium">Juridische verplichting:</span> Omwille van juridische redenen moet de eerste taal altijd de voertaal van het landsdeel zijn (vb. Nederlands in Vlaanderen).
        </p>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {LANGUAGES.map(({ code, label }) => (
          <div key={code} className="flex items-center space-x-2">
            <Checkbox
              id={`lang-${code}`}
              checked={selectedLanguages.includes(code)}
              onCheckedChange={() => toggleLanguage(code)}
              disabled={isTranslating}
            />
            <Label htmlFor={`lang-${code}`} className="text-sm text-gray-700 cursor-pointer">
              {label}
            </Label>
          </div>
        ))}
      </div>

      <Button
        onClick={handleTranslate}
        disabled={selectedLanguages.length === 0 || !emailContent.trim() || isTranslating}
        size="sm"
      >
        {isTranslating ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Bezig met vertalen...
          </>
        ) : (
          'Vertalen'
        )}
      </Button>
    </div>
  );
};
